import { JsonObject, JsonValue } from "type-fest";
import { z } from "zod";
import type { Unzipped } from "fflate";
import { wktToGeoJSON } from "betterknown";
import { rough } from "src/lib/geo/roughly_geojson";
import { polylineToGeoJSON } from "@placemarkio/polyline";


export async function unzip(file: ArrayBuffer) {
  const fflate = await import("fflate");
  return await new Promise<Unzipped>((resolve, reject) => {
    fflate.unzip(new Uint8Array(file), (err, res) => {
      if (err) return reject(err);
      resolve(res);
    });
  });
}

export const EnforcedWKTOptions = z.object({
  geometryHeader: z.string(),
});

export const EnforcedLonLatOptions = z.object({
  latitudeHeader: z.string(),
  longitudeHeader: z.string(),
  autoType: z.boolean(),
});

function safeParse(value: JsonValue | undefined) {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return NaN;
  return parseFloat(value);
}

export function castRowWKT(
  parsedRow: JsonObject,
  options: z.infer<typeof EnforcedWKTOptions>
): GeoJSON.Feature | null {
  const { geometryHeader } = options;
  // Numbers.app doesn't prune columns without headers.
  delete parsedRow[""];

  const geometry = wktToGeoJSON(String(parsedRow[geometryHeader]));
  if (!geometry) return null;
  delete parsedRow[geometryHeader];
  return {
    type: "Feature",
    properties: parsedRow,
    geometry,
  };
}

export function castRowPolyline(
  parsedRow: JsonObject,
  options: z.infer<typeof EnforcedWKTOptions>
): GeoJSON.Feature | null {
  const { geometryHeader } = options;
  // Numbers.app doesn't prune columns without headers.
  delete parsedRow[""];
  const geometry = polylineToGeoJSON(String(parsedRow[geometryHeader]));
  delete parsedRow[geometryHeader];
  return {
    type: "Feature",
    properties: parsedRow,
    geometry,
  };
}

export function castRowGeoJSON(
  parsedRow: JsonObject,
  options: z.infer<typeof EnforcedWKTOptions>
): GeoJSON.Feature | null {
  const { geometryHeader } = options;
  // Numbers.app doesn't prune columns without headers.
  delete parsedRow[""];

  const value = JSON.parse(String(parsedRow[geometryHeader]));
  const geojsonResult = rough(value);
  const geometry = geojsonResult.geojson.features[0]?.geometry;
  delete parsedRow[geometryHeader];
  return {
    type: "Feature",
    properties: parsedRow,
    geometry,
  };
}

export function castRowLonLat(
  parsedRow: JsonObject,
  options: z.infer<typeof EnforcedLonLatOptions>
): GeoJSON.Feature | null {
  const { latitudeHeader, longitudeHeader } = options;
  const lon = safeParse(parsedRow[longitudeHeader]);
  const lat = safeParse(parsedRow[latitudeHeader]);
  // Numbers.app doesn't prune columns without headers.
  delete parsedRow[""];

  if (isNaN(lon) || isNaN(lat)) {
    // TODO: handle errors
    // errors.push({
    //   message: "A row contained an invalid value for latitude or longitude",
    //   row: parsed[i],
    //   index: i,
    // });
  } else {
    delete parsedRow[longitudeHeader];
    delete parsedRow[latitudeHeader];

    return {
      type: "Feature",
      properties: parsedRow,
      geometry: {
        type: "Point",
        coordinates: [lon, lat],
      },
    };
  }
  return null;
}
